/**
 * Maps portal estimateCalculator config (merged defaults + userPlugins customizations)
 * to the JSON shape consumed by the Estimate & Rendering Calculator importer (rba_* keys).
 */

const ALLOWED_OPS = ['×', '+', '−', '÷'];

function clampPercent(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return 15;
  return Math.max(0, Math.min(100, Math.round(x)));
}

/** Normalize operator row to exactly four × + − ÷ characters */
function sanitizeOperators(ops) {
  const raw = Array.isArray(ops) ? [...ops] : [];
  const out = [];
  for (let i = 0; i < 4; i++) {
    let c = raw[i];
    if (typeof c === 'string') {
      c = c.trim();
      if (c === '-' || c === '\u2013') c = '\u2212';
      if (c === 'x' || c === 'X' || c === '*') c = '\u00d7';
    }
    out.push(ALLOWED_OPS.includes(c) ? c : ALLOWED_OPS[i]);
  }
  return out;
}

function splitLocationLines(s) {
  if (s == null || String(s).trim() === '') return [];
  return String(s)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function buildWebhookUrls(settings) {
  if (!settings || settings.webhookNotApplicable) return [];
  const url = String(settings.webhookUrl || '').trim();
  if (!url) return [];
  return url.split(/\r?\n/).map((u) => u.trim()).filter(Boolean);
}

function mapCompletionScreen(erp) {
  if (!erp || typeof erp !== 'object') return {};
  return {
    estimate_title: erp.estimateTitle ?? '',
    estimate_subtitle: erp.estimateSubtitle ?? '',
    reimagined_title: erp.reimaginedSectionTitle ?? '',
    vision_title: erp.yourVisionTitle ?? '',
    vision_summary_template: erp.visionSummaryTemplate ?? '',
    estimated_investment_title: erp.estimatedInvestmentTitle ?? '',
    location_note: erp.locationNoteText ?? '',
    preliminary_note: erp.preliminaryEstimateNote ?? '',
    get_started_title: erp.getStartedTitle ?? '',
    get_started_subtitle: erp.getStartedSubtitle ?? '',
    call_phone: erp.callPhoneDigits ?? '',
    schedule_button_url: erp.scheduleConsultationLink ?? '',
    location_note_enabled: !!erp.locationNoteEnabled,
    location_in_range: splitLocationLines(erp.locationInRangeList)
  };
}

function buildQuestionsArray(cfg) {
  const q1 = cfg.question1 || {};
  const q2 = cfg.question2 || {};
  const q7 = cfg.question7 || {};
  const q9 = cfg.question9 || {};

  const standard = {
    type: 'standard',
    question: q1.headerText ?? '',
    subheader: '',
    options: (q1.options || []).map((o) => ({
      text: o.label ?? '',
      base_cost: Number(o.baseCost) || 0
    }))
  };

  const propertyType = {
    type: 'property_type',
    question: q2.headerText ?? '',
    subheader: '',
    options: (q2.options || []).map((o) => ({
      text: o.label ?? '',
      reject: !!o.reject
    }))
  };

  const whenToStart = {
    type: 'when_to_start',
    question: q7.headerText ?? '',
    subheader: '',
    options: (q7.options || []).map((o) =>
      typeof o === 'string' ? o : (o && o.label) || ''
    )
  };

  const locationInput = {
    type: 'location_input',
    question: q9.headerText ?? '',
    placeholder: q9.placeholderText ?? '',
    subheader: ''
  };

  return [standard, propertyType, whenToStart, locationInput];
}

function buildQuestionRouting(routing, question1) {
  if (!routing || typeof routing !== 'object') return {};
  const q1Labels = new Set(
    (question1?.options || []).map((o) => String(o.label || '').trim())
  );
  const out = {};

  // Routes are keyed off Question 1's options, so custom project types show up here too.
  for (const key of Object.keys(routing)) {
    const bundle = routing[key];
    if (!bundle?.questions || bundle.questions.length !== 4) continue;

    const routeKeyName = String(bundle.routeLabel || '').trim();
    if (!routeKeyName || !q1Labels.has(routeKeyName)) continue;

    out[routeKeyName] = {
      questions: bundle.questions.map((q) => ({
        question: q.questionText ?? '',
        options: (q.options || []).map((o) => ({
          text: o.label ?? '',
          value: typeof o.value === 'number' ? o.value : Number(o.value) || 0
        }))
      }))
    };
  }

  return out;
}

function buildEstimateFormulas(estimateFormulas, question1) {
  if (!estimateFormulas || typeof estimateFormulas !== 'object') return {};
  const labels = new Set(
    (question1?.options || []).map((o) => String(o.label || '').trim())
  );
  const out = {};

  for (const entry of Object.values(estimateFormulas)) {
    const label = String(entry?.label || '').trim();
    if (!label || !labels.has(label)) continue;
    out[label] = {
      routing_ref: entry.routingRef ?? '',
      operators: sanitizeOperators(entry.operators)
    };
  }

  return out;
}

function buildPhotoUpload(question8) {
  if (!question8 || typeof question8 !== 'object') return {};
  return {
    header_text: question8.headerText ?? '',
    disclaimer_text: question8.imageDisclaimerText ?? ''
  };
}

function buildUnlock(question10) {
  if (!question10 || typeof question10 !== 'object') return {};
  return {
    title: question10.title ?? '',
    button_text: question10.buttonText ?? '',
    fields: (question10.fields || []).map((f) => ({
      id: f.id,
      label: f.label ?? '',
      placeholder: f.placeholder ?? '',
      required: !!f.required
    })),
    content_checkbox_enabled: !!question10.contentCheckboxEnabled,
    content_checkbox_text: question10.contentCheckboxText ?? ''
  };
}

/**
 * @param {object} mergedConfig — output of deepMergeEstimateConfig(defaults, saved)
 * @returns {object} Single JSON object with rba_* keys for plugin importer
 */
export function buildEstimateCalculatorRbaImportJson(mergedConfig) {
  const cfg = mergedConfig && typeof mergedConfig === 'object' ? mergedConfig : {};
  const settings = cfg.settings || {};

  const payload = {
    rba_openai_api_key: String(settings.apiKey ?? '').trim(),
    rba_openai_model: String(settings.openaiImageModel ?? 'gpt-image-1').trim(),
    rba_webhook_urls: buildWebhookUrls(settings),
    rba_estimate_range: clampPercent(cfg.estimateRangePercent),
    rba_completion_screen_content: mapCompletionScreen(cfg.estimateReadyPage),
    rba_questions: buildQuestionsArray(cfg),
    rba_rejection_settings: cfg.question2?.rejection ? { ...cfg.question2.rejection } : {},
    rba_question_routing: buildQuestionRouting(cfg.routing, cfg.question1),
    rba_photo_upload_settings: buildPhotoUpload(cfg.question8),
    rba_unlock_settings: buildUnlock(cfg.question10),
    rba_estimate_formulas: buildEstimateFormulas(cfg.estimateFormulas, cfg.question1)
  };

  return payload;
}
